import {useEffect, useRef} from "react";
import {useImages} from "contexts/images";
import {useSetting} from "contexts/setting";
import {getLogoCycle, isBlackLogoUrl} from "libs/logo";
import {getTemplateScopeMeta} from "libs/logoOverrides";
import {useFeatures} from "hooks/useFeature";

export function useLogoCycling(scopeRef) {
  const {logoVariants, textColorLocked, editLogoVariant, editTextColorLocked} = useImages();
  const {product, type, layoutOption, channel} = useSetting();

  const productRef = useRef(product);
  const typeRef = useRef(type);
  const layoutOptionRef = useRef(layoutOption);
  const channelRef = useRef(channel);
  const logoVariantsRef = useRef(logoVariants);
  const textColorLockedRef = useRef(textColorLocked);
  // Role features (data/features.ts): "banner.logo-cycle" for an Alt+click on
  // a logo, "banner.text-recolor" for an Alt+click on text. Without one the
  // click does what a plain click does.
  const features = useFeatures();
  const logoCycleRef = useRef(features["banner.logo-cycle"]);
  const textRecolorRef = useRef(features["banner.text-recolor"]);
  useEffect(() => {
    logoCycleRef.current = features["banner.logo-cycle"];
    textRecolorRef.current = features["banner.text-recolor"];
  }, [features]);

  useEffect(() => {
    productRef.current = product;
  }, [product]);
  useEffect(() => {
    typeRef.current = type;
  }, [type]);
  useEffect(() => {
    layoutOptionRef.current = layoutOption;
  }, [layoutOption]);
  useEffect(() => {
    channelRef.current = channel;
  }, [channel]);
  useEffect(() => {
    logoVariantsRef.current = logoVariants;
  }, [logoVariants]);
  useEffect(() => {
    textColorLockedRef.current = textColorLocked;
  }, [textColorLocked]);

  useEffect(() => {
    function handleClick(e) {
      if (!e.altKey) return;

      const target = e.target instanceof Element ? e.target : e.target?.parentElement;
      if (!target || (scopeRef && !scopeRef.current?.contains(target))) return;

      const template = target.closest(".template");
      if (!template?.id) return;

      const id = template.id;

      // The header logo cycle only applies when clicking a logo/product-name in
      // a header that actually renders a logo. Anything else (e.g. a logo-less
      // banner like Blog Featured) falls through to the text-recolor path below.
      const gdnLogo = target.closest('[data-logo-cycle-target="gdn"]');
      const clickableHeaderEl = gdnLogo || target.closest(".logo, .product-name");
      const header = clickableHeaderEl?.closest(".template-header");
      const canCycleLogo =
        Boolean(gdnLogo) || (clickableHeaderEl && header && header.querySelector(".logo"));

      if (canCycleLogo && !logoCycleRef.current) return;
      if (!canCycleLogo) {
        if (!textRecolorRef.current) return;
        // Alt+Click on the banner text toggles its color independently of the
        // logo — the only way to recolor text on logo-less banners, and a handy
        // per-template override elsewhere. Editing is suppressed while Alt is
        // held (see the alt-recolor-mode effect) so the click lands here instead
        // of focusing the input. Header text is handled by the logo path above.
        const textEl = target.closest("[data-field-id], .content, .title, .subtitle");
        if (!textEl || textEl.closest(".template-header")) return;

        e.preventDefault();
        // Cycle: default (follow logo/bg) → light → black → default.
        const current = textColorLockedRef.current[id] ?? null;
        const next = current === null ? "light" : current === "light" ? "black" : null;
        editTextColorLocked(id, next);
        return;
      }

      e.preventDefault();

      const meta = getTemplateScopeMeta(id);
      const productValue = meta?.productValue ?? productRef.current?.value;
      const typeValue = meta?.typeValue ?? typeRef.current?.value;
      const layout = meta?.layoutOption ?? layoutOptionRef.current;
      const channelValue = meta?.channelValue ?? channelRef.current?.value;
      const variants = logoVariantsRef.current;

      const cycle = getLogoCycle(productValue, typeValue, layout, channelValue);
      if (!Array.isArray(cycle) || cycle.length === 0) return;

      // Current effective URL: override or auto-detect
      const currentUrl = variants[id] || null;

      // Find current index in cycle (null = auto-detect = index 0)
      let currentIndex = 0;
      if (currentUrl) {
        const found = cycle.indexOf(currentUrl);
        currentIndex = found >= 0 ? found : 0;
      }

      const nextIndex = (currentIndex + 1) % cycle.length;
      const nextValue = cycle[nextIndex]; // null=auto-detect, LOGO_VARIANT_NONE=no logo

      // Alt+Shift+Click: cycle logo only, freeze text color at its current value.
      // If already locked, keep the existing lock — don't re-derive from the
      // (now different) logo URL, which would shift the text color on each click.
      if (e.shiftKey) {
        const alreadyLocked = textColorLockedRef.current[id];
        if (!alreadyLocked) {
          const currentLogoUrl = variants[id];
          const isCurrentlyBlack = currentLogoUrl ? isBlackLogoUrl(currentLogoUrl) : false;
          editTextColorLocked(id, isCurrentlyBlack ? "black" : "light");
        }
      } else {
        // Alt+Click: normal sync mode, unlock text color.
        editTextColorLocked(id, null);
      }

      editLogoVariant(id, nextValue);
    }

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [editLogoVariant, editTextColorLocked]);

  // While Option/Alt is held, mark the body so text inputs become click-through
  // (CSS in App.css). This lets the recolor click above land on the text element
  // instead of focusing the editor. Toggled directly on the DOM (not React state)
  // so the class is present synchronously before the click fires.
  useEffect(() => {
    const CLASS = "alt-recolor-mode";
    const setActive = (active) => document.body.classList.toggle(CLASS, active);
    // Only for a role with the text recolor: else the text stays editable.
    const onKeyDown = (e) => setActive(e.altKey && textRecolorRef.current);
    const onKeyUp = (e) => setActive(e.altKey && textRecolorRef.current);
    // Clear if the window loses focus while Alt is down — keyup would never fire.
    const clear = () => setActive(false);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", clear);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", clear);
      document.body.classList.remove(CLASS);
    };
  }, []);
}
